import React, { FC, useMemo, useState } from "react";
import DatePicker, { ReactDatePickerCustomHeaderProps, registerLocale } from "react-datepicker";
import { getMonth, getYear } from "date-fns";
import dateFnsFormat from "date-fns/format";
import pl from "date-fns/locale/pl";
import styled, { css } from "styled-components/macro";
import { t } from "../../utils/intl";

import {
  mainDateDisplayFormat,
  mainDateTimeDisplayFormat,
  mainDateValueFormat,
  mainDateTimeValueFormat,
  mapLocale
} from "../../utils/dateUtils";
import { defaultTheme } from "../../theme/defaultTheme";
import MainSelect from "./Select";
import { Icon } from "./Icon";
import { ILabelProps, Label } from "./Label";
import "react-datepicker/dist/react-datepicker.css";
import { InvalidMessage } from "./styled";

const commonSelectProps = {
  customStylesExtension: {
    container: {
      fontSize: "14px",
      fontWeight: 600
    },
    control: {
      padding: "8px",
      border: "none",
      minHeight: "unset",
      boxShadow: `inset 0px 0px 0px 1px ${defaultTheme.newColors.gray30}`
    },
    menu: {
      border: "none"
    },
    option: {
      borderTop: "unset",
      padding: "4px 8px",

      ":hover": {
        color: defaultTheme.newColors.primary100,
        backgroundColor: defaultTheme.newColors.gray10
      }
    }
  }
};

interface IDatepickerProps {
  initialValue?: string;
  dateDisplayFormat?: string;
  dateTimeDisplayFormat?: string;
  dateValueFormat?: string;
  dateTimeValueFormat?: string;
  showTimeSelect?: boolean;
  timeIntervals?: number;
  minYear?: number;
  maxYear?: number;
  value?: string;
  onChange: (value: string) => void;
  onFocus?: React.FocusEventHandler<HTMLInputElement> & ((e?: any) => void);
  disabled?: boolean;
  isInvalid?: boolean;
  placeholder?: string;
  labelProps?: ILabelProps;
  isInvalidMessage: string;
  onBlur: any;
  rowNumber: number;
  labelTrKey: string;
  id?: string;
}

registerLocale("pl", pl);

export const Datepicker: FC<IDatepickerProps> = props => {
  const {
    value,
    initialValue,
    dateDisplayFormat,
    dateTimeDisplayFormat,
    dateValueFormat,
    dateTimeValueFormat,
    showTimeSelect,
    timeIntervals,
    labelTrKey,
    rowNumber,
    minYear,
    maxYear,
    onFocus,
    onBlur,
    isInvalidMessage,
    onChange,
    isInvalid,
    disabled,
    placeholder
  } = props;

  const [isDatepickerOpen, setIsDatepickerOpen] = useState<boolean>(false);

  const months = useMemo(() => [...Array(12)].map(mapLocale(pl, "month", "wide")), []);
  const years = useMemo(
    () => [...Array(maxYear! - minYear! + 1)].map((_, index) => maxYear! - index),
    [minYear, maxYear]
  );

  const getCustomHeader = useMemo(
    () => ({
      date,
      changeYear,
      changeMonth,
      decreaseMonth,
      increaseMonth
    }: ReactDatePickerCustomHeaderProps) => {
      const leftArrowDisabled = getYear(date) === minYear && getMonth(date) === 0;
      const rightArrowDisabled = getYear(date) === maxYear && getMonth(date) === 11;

      const monthSelectOptions = months.map(month => ({ value: month, label: month }));
      const yearSelectOptions = years.map(year => ({ value: year, label: year }));

      return (
        <CustomHeaderWrapper>
          <ArrowWrapper tabIndex={0}>
            <ArrowIcon
              name="chevron-left"
              onClick={leftArrowDisabled ? undefined : decreaseMonth}
              disabled={leftArrowDisabled}
              width={20}
            />
          </ArrowWrapper>
          <MonthDatepickerSelect
            {...commonSelectProps}
            value={monthSelectOptions.find(option => option.value === months[getMonth(date)])}
            options={monthSelectOptions}
            onChange={({ value }) => changeMonth(months.indexOf(value))}
          />
          <YearsDatepickerSelect
            {...commonSelectProps}
            value={yearSelectOptions.find(option => option.value === getYear(date))}
            options={yearSelectOptions}
            onChange={({ value }) => changeYear(Number(value))}
          />
          <ArrowWrapper tabIndex={0}>
            <ArrowIcon
              name="chevron-right"
              onClick={rightArrowDisabled ? undefined : increaseMonth}
              disabled={rightArrowDisabled}
              width={20}
            />
          </ArrowWrapper>
        </CustomHeaderWrapper>
      );
    },
    [minYear, maxYear, months, years]
  );

  const validDateDisplayFormat = showTimeSelect ? dateTimeDisplayFormat : dateDisplayFormat;
  const validDateValueFormat = showTimeSelect ? dateTimeValueFormat : dateValueFormat;

  return (
    <>
      <Label labelTrKey={labelTrKey} rowNumber={rowNumber} />
      <Container isInvalid={isInvalid} isDatepickerOpen={isDatepickerOpen}>
        <DatepickerIcon name="calendar" width={20} color="gray100" />
        <DatePicker
          openToDate={initialValue && !value ? new Date(initialValue) : undefined}
          selected={value ? new Date(value) : undefined}
          onChange={(datepickerValue: Date) => {
            onChange(datepickerValue ? dateFnsFormat(datepickerValue, validDateValueFormat!) : "");
          }}
          onChangeRaw={e => {
            if (e.target.nodeName === "DIV") setIsDatepickerOpen(false);
          }}
          locale="pl"
          onFocus={() => {
            setIsDatepickerOpen(true);
            onFocus?.();
          }}
          onBlur={() => {
            setIsDatepickerOpen(false);
            onBlur?.(value);
          }}
          dateFormat={validDateDisplayFormat}
          showTimeSelect={showTimeSelect}
          timeCaption={t("common.datepicker.timeCaption")}
          timeIntervals={timeIntervals}
          renderCustomHeader={getCustomHeader}
          disabled={disabled}
          placeholderText={placeholder ? t(placeholder) : undefined}
          popperPlacement="bottom-end"
        />
      </Container>
      {isInvalid && isInvalidMessage && <InvalidMessage>{t(isInvalidMessage)}</InvalidMessage>}
    </>
  );
};

Datepicker.defaultProps = {
  dateDisplayFormat: mainDateDisplayFormat,
  dateTimeDisplayFormat: mainDateTimeDisplayFormat,
  dateValueFormat: mainDateValueFormat,
  dateTimeValueFormat: mainDateTimeValueFormat,
  timeIntervals: 15,
  minYear: 1920,
  maxYear: getYear(new Date())
};

const DatepickerIcon = styled(Icon)`
  position: absolute;
  top: 0;
  right: 0;
  pointer-events: none;
  z-index: 1;
  padding: 16px;
  border-left: 1px solid ${({ theme }) => theme.newColors.gray30};
`;

const Container = styled.div<{ isInvalid?: boolean; isDatepickerOpen: boolean }>`
  position: relative;

  ${({ isInvalid, theme }) =>
    isInvalid &&
    css`
      ${DatepickerIcon} {
        border-left: 2px solid ${theme.newColors.error};
      }
    `}

  ${({ isDatepickerOpen, theme }) =>
    isDatepickerOpen &&
    css`
      ${DatepickerIcon} {
        border-left: 2px solid ${theme.newColors.blue};
      }
    `}

  &:hover {
    ${DatepickerIcon} {
      border-left: 2px solid ${({ theme }) => theme.newColors.blue};
    }
  }

  .react-datepicker {
    font-family: unset;
    font-size: 14px;
    line-height: 20px;
    font-weight: 600;
    border: solid 2px ${({ theme }) => theme.colors.blue};
    border-radius: 0;
    color: ${({ theme }) => theme.newColors.gray100};
    box-shadow: ${({ theme }) => theme.shadows.elevation5};
    margin-top: -2px;
    margin-bottom: -2px;
    display: flex;

    & *:focus {
      outline: 0;
    }

    &__input-container input {
      width: 100%;
      height: 52px;
      background: ${({ theme }) => theme.newColors.white};
      border: 0;
      box-shadow: inset 0px 0px 0px 1px ${({ theme }) => theme.newColors.gray30};
      text-align: left;
      color: ${({ theme }) => theme.newColors.gray100};
      -webkit-text-fill-color: ${({ theme }) => theme.newColors.gray100};
      background-clip: padding-box;
      position: relative;
      padding: 0 12px;
      padding-right: 68px;
      font-size: 18px;
      line-height: 20px;
      font-weight: 600;

      &::placeholder {
        color: ${({ theme }) => theme.newColors.gray60};
        -webkit-text-fill-color: ${({ theme }) => theme.newColors.gray60};
        opacity: 1;
      }

      &:disabled {
        color: ${({ theme }) => theme.newColors.gray80};
        -webkit-text-fill-color: ${({ theme }) => theme.newColors.gray80};
        background: ${({ theme }) => theme.newColors.gray10};
        cursor: not-allowed;
      }

      ${({ isInvalid, theme }) =>
        isInvalid &&
        css`
          box-shadow: inset 0px 0px 0px 2px ${theme.newColors.error};
        `}

      &:hover:not(:focus):not(:disabled) {
        box-shadow: inset 0px 0px 0px 2px ${defaultTheme.colors.blue};
      }

      ${({ isDatepickerOpen, theme }) =>
        isDatepickerOpen &&
        css`
          box-shadow: inset 0px 0px 0px 2px ${defaultTheme.colors.blue};
        `}

      &:focus {
        outline: 0;
      }
    }

    &__triangle {
      display: none;
    }

    &-popper {
      padding: 0 !important;

      z-index: 19;
    }

    &__header {
      border-bottom: 0;
      padding: 10px 12px 12px;
      border-radius: 0;
    }

    &__day-names {
      display: flex;
      justify-content: space-around;
      padding: 0;
      margin: 0;
      font-size: 12px;
      line-height: 16px;
      font-weight: 400;
    }

    &__day-name {
      text-transform: capitalize;
      color: ${({ theme }) => theme.newColors.gray80};
      width: 40px;
      height: 16px;
      line-height: inherit;
      margin: 0;
    }

    &__month {
      padding: 8px 12px 12px;
      margin: 0;
    }

    &__day {
      margin: 0;
      padding: 0;
      width: 40px;
      height: 40px;
      line-height: 40px;
      border-radius: 0;
      font-weight: 600;

      &--keyboard-selected {
        color: unset;
        background-color: unset;
      }

      &--today {
        color: ${({ theme }) => theme.newColors.primary80};
      }

      &--outside-month {
        color: ${({ theme }) => theme.newColors.gray60};
      }

      &--selected {
        color: ${({ theme }) => theme.newColors.white};
        background-color: ${({ theme }) => theme.newColors.primary100};
      }

      &:hover {
        color: ${({ theme }) => theme.newColors.primary100};
        background-color: ${({ theme }) => theme.newColors.gray20};
      }
    }

    &__header--time {
      padding: 14px 12px;
    }

    &-time__header {
      font-weight: 600;
      font-size: 16px;
      line-height: 20px;
    }

    & .react-datepicker__time-container {
      border-left: 1px solid ${({ theme }) => theme.newColors.gray30};
      width: unset;

      & .react-datepicker__time .react-datepicker__time-box {
        width: unset;

        & ul.react-datepicker__time-list {
          width: 108px;

          &::-webkit-scrollbar {
            width: 8px;
            height: 0px;
          }

          &::-webkit-scrollbar-track {
            background-color: ${({ theme }) => theme.newColors.gray10};
          }
          &::-webkit-scrollbar-thumb {
            background-color: ${({ theme }) => theme.newColors.gray40};
          }

          & li.react-datepicker__time-list-item {
            height: 24px;
            padding: 2px 0;
            box-sizing: border-box;

            &:hover {
              color: ${({ theme }) => theme.newColors.primary100};
              background-color: ${({ theme }) => theme.newColors.gray20};
            }

            &--selected,
            &--selected:hover {
              color: ${({ theme }) => theme.newColors.white};
              background-color: ${({ theme }) => theme.newColors.primary100};
            }
          }
        }
      }
    }
  }
`;

const CustomHeaderWrapper = styled.div`
  display: flex;
  padding-bottom: 12px;
`;

const ArrowWrapper = styled.div`
  display: flex;
  align-items: center;
`;

const DatepickerSelect = styled(MainSelect)`
  border: solid 1px ${({ theme }) => theme.newColors.gray30};
  margin: 0;
`;

const MonthDatepickerSelect = styled(DatepickerSelect)`
  flex-grow: 1;
`;

const YearsDatepickerSelect = styled(DatepickerSelect)`
  flex-basis: 80px;
`;

const ArrowIcon = styled(Icon)<{ disabled: boolean }>`
  padding: 8px 4px;

  &:hover {
    svg path {
      stroke: ${({ theme }) => theme.newColors.primary80};
    }
  }

  ${({ disabled }) =>
    disabled &&
    css`
      cursor: not-allowed;

      && svg path {
        stroke: ${({ theme }) => theme.newColors.gray80};
      }
    `}
`;
